/**
 * The item marketplace's listing lifecycle, and what may be sorted or filtered.
 *
 * Platform-neutral and pure. The server owns every transition and every clock
 * reading; this only says which transitions exist and what a query may ask for.
 *
 * Deliberately much simpler than the direct trade's state machine, because a
 * listing is not a negotiation: it is an offer at a fixed price that somebody
 * either takes or does not. There is no revision, no double acceptance and no
 * counterparty until the instant of sale.
 */

export const LISTING_STATES = [
  /** On sale. The only state a purchase may act on. */
  'active',
  /** Bought. Terminal. */
  'sold',
  /** Withdrawn by the seller. Terminal. */
  'cancelled',
  /** The seven days ran out. Terminal. */
  'expired',
] as const;
export type ListingState = (typeof LISTING_STATES)[number];

export const TERMINAL_LISTING_STATES: readonly ListingState[] = Object.freeze([
  'sold',
  'cancelled',
  'expired',
]);

export function isTerminalListingState(state: ListingState): boolean {
  return TERMINAL_LISTING_STATES.includes(state);
}

/**
 * Every transition that exists. Anything absent is a refusal.
 *
 * Note what `active` cannot do: it cannot become another `active` with a
 * different price. Editing a listing would let a seller change what a buyer is
 * looking at between the click and the commit, so a price change is a cancel
 * and a new listing - which is honest, cheap (there is no fee for either) and
 * leaves the old listing in the history where it belongs.
 */
const TRANSITIONS: Readonly<Record<ListingState, readonly ListingState[]>> = Object.freeze({
  active: Object.freeze(['sold', 'cancelled', 'expired'] as const),
  sold: Object.freeze([] as const),
  cancelled: Object.freeze([] as const),
  expired: Object.freeze([] as const),
});

export function canTransitionListing(from: ListingState, to: ListingState): boolean {
  return TRANSITIONS[from].includes(to);
}

/**
 * How long a listing stands before it expires. Seven days.
 *
 * Long, because a listing holds the item and a seller who priced optimistically
 * should not have to re-post every hour; bounded, because an abandoned listing
 * holds an item nobody can use. Measured on the SERVER's clock.
 */
export const LISTING_TTL_MS = 7 * 24 * 60 * 60_000;

/** How much a listing may be worth. Guards the ledger's declared capacity. */
export const LISTING_MIN_PRICE = 1n;

/** Page size bounds for the public browse. */
export const MARKET_PAGE_SIZE_DEFAULT = 24;
export const MARKET_PAGE_SIZE_MAX = 60;

/**
 * How a browse may be ordered.
 *
 * Every one of these is made STABLE by appending the listing id as the final
 * tie-break. Without it, two listings created in the same millisecond - or at
 * the same price, which is far more likely - could swap places between pages
 * and a buyer would see one twice and another never.
 */
export const MARKET_SORTS = ['recent', 'price_asc', 'price_desc'] as const;
export type MarketSort = (typeof MARKET_SORTS)[number];

/**
 * Everything that ends a listing without a sale.
 *
 * Costs nothing, always. There is no fee to create a listing, to cancel one or
 * to let one expire - the only charge in this system is the percentage a
 * completed sale takes out of the seller's proceeds.
 */
export const LISTING_CLOSE_REASONS = ['cancelled', 'expired'] as const;
export type ListingCloseReason = (typeof LISTING_CLOSE_REASONS)[number];

/** Append-only audit vocabulary for a listing. */
export const MARKET_EVENT_TYPES = [
  'listed',
  'sold',
  'cancelled',
  'expired',
  'purchase_failed',
] as const;
export type MarketEventType = (typeof MARKET_EVENT_TYPES)[number];
